import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Integration } from "../../../types/integration";
import { CustomIntegrationTile } from "../../../components/dashboard/integrations/CustomIntegrationTile";
import { Skeleton } from "../../../components/dashboard/Skeleton";
import { IntegrationCard } from "../../../components/dashboard/integrations/IntegrationCard";
import { ViewAllTile } from "../../../components/dashboard/integrations/ViewAllTile";
import styles from "./Integrations.module.css";

const CATALOG_PATH = "/dashboard/integrations" as const;

// Number of preview cards in the teaser strip (excludes the custom +
// view-all tiles).
const PREVIEW_TARGET_COUNT: number = 10;

// The curated teaser lineup — commonly-used tools our (mostly non-technical)
// customers already use day to day, in display order. Sized to fill the strip
// on its own so the featured top-up below is just a safety net. The full,
// searchable set lives on the catalog page. Slugs not present in the fetched
// catalog are silently skipped, so the trailing entries act as buffer.
const PREVIEW_SLUGS: ReadonlyArray<string> = [
  "gmail",
  "googlecalendar",
  "outlook",
  "notion",
  "hubspot",
  "canva",
  "metaads",
  // Buffer: only surface if one of the above is missing from the catalog.
  "stripe",
  "xero",
  "googledrive",
  "airtable",
  "asana",
];

type Props = {
  integrations: ReadonlyArray<Integration>;
  loading?: boolean;
  error?: string | null;
};

export const IntegrationsSection = ({
  integrations,
  loading = false,
  error = null,
}: Props): JSX.Element => {
  const navigate = useNavigate();

  // The teaser shows the curated lineup in order (connected integrations are
  // intentionally NOT prioritised here — they already surface in the "Active
  // integrations" panel above). The featured-first catalog only tops up if the
  // curated list somehow comes up short.
  const preview = useMemo<ReadonlyArray<Integration>>(() => {
    const bySlug = new Map<string, Integration>(
      integrations.map((i) => [i.slug, i]),
    );
    const chosen = new Map<string, Integration>();
    // 1. The curated, commonly-used lineup, in order.
    for (const slug of PREVIEW_SLUGS) {
      const i = bySlug.get(slug);
      if (i !== undefined && !chosen.has(slug)) chosen.set(slug, i);
    }
    // 2. Safety net: top up from the rest of the (featured-first) catalog if
    //    the curated list didn't fill the strip (e.g. a slug missing from sync).
    for (const i of integrations) {
      if (chosen.size >= PREVIEW_TARGET_COUNT) break;
      if (!chosen.has(i.slug)) chosen.set(i.slug, i);
    }
    return Array.from(chosen.values()).slice(0, PREVIEW_TARGET_COUNT);
  }, [integrations]);

  const renderBody = (): JSX.Element => {
    if (loading) {
      // Cold first load only — cached catalogs report ready immediately.
      return (
        <>
          {Array.from({ length: PREVIEW_TARGET_COUNT + 1 }, (_, i) => (
            <div key={i} className={styles.skeletonCard} aria-hidden>
              <Skeleton width="40px" height="40px" radius="10px" />
              <Skeleton width="60%" height="0.9rem" />
              <Skeleton width="40%" height="0.7rem" />
            </div>
          ))}
        </>
      );
    }
    if (error !== null) {
      return <div className={styles.empty}>Couldn't load integrations.</div>;
    }
    return (
      <>
        {integrations.length > 0 ? (
          <ViewAllTile
            count={integrations.length}
            onClick={(): void => navigate(CATALOG_PATH)}
          />
        ) : null}
        {preview.map((integration) => (
          <IntegrationCard key={integration.slug} integration={integration} />
        ))}
        <CustomIntegrationTile />
      </>
    );
  };

  return (
    <section
      className={styles.section}
      aria-labelledby="dashboard-integrations-title"
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.eyebrow}>// INTEGRATIONS · PREVIEW</span>
          <h2 className={styles.title} id="dashboard-integrations-title">
            Plug it all in.
          </h2>
        </div>
        <button
          type="button"
          className={styles.viewAllLink}
          onClick={(): void => navigate(CATALOG_PATH)}
        >
          Open the catalog &rarr;
        </button>
      </div>
      <div className={styles.grid}>{renderBody()}</div>
    </section>
  );
};
